import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  PeopleIcon,
  BedIcon,
  BathtubIcon,
  WifiIcon,
  AcUnitIcon,
  KitchenIcon,
  FireplaceIcon,
  BalconyIcon,
  AspectRatioIcon,
} from '../../../common/icons/Icons';
import { usePropertyId } from '../../../common/hooks/usePropertyId';
import { useRoomsData } from '../../../common/hooks/useRoomsData';
import { useRoomPicturesData } from '../../../common/hooks/useRoomPicturesData';
import { withQueryData } from '../../../common/withQueryData';
import withWebsiteContent from '../../../common/withWebsiteContent';
import { sectionScrollMarginSx } from '../../../common/hooks/useScrollSpyHash';

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Gets a field value from an item by matching field name (case-insensitive, handles spaces/dashes)
 */
const getFieldValue = (item, fieldName) => {
  if (!item || typeof item !== 'object') return undefined;
  if (item[fieldName] !== undefined) return item[fieldName];
  const lowerFieldName = fieldName.toLowerCase();
  for (const key in item) {
    if (key.toLowerCase() === lowerFieldName) return item[key];
  }
  const normalizedFieldName = fieldName.toLowerCase().replace(/[\s-]/g, '');
  for (const key in item) {
    const normalizedKey = key.toLowerCase().replace(/[\s-]/g, '');
    if (normalizedKey === normalizedFieldName) return item[key];
  }
  return undefined;
};

/**
 * Polish pluralization helper.
 */
const getPolishPlural = (value, singular, plural1, plural2) => {
  if (typeof value !== 'number') return singular;
  const lastDigit = value % 10;
  const lastTwoDigits = value % 100;
  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return plural2;
  if (value === 1) return singular;
  if (lastDigit >= 2 && lastDigit <= 4) return plural1;
  return plural2;
};

/**
 * Normalizes accommodations API response (kept for backward compatibility with other consumers).
 */
const normalizeAccommodationsContent = (data) => {
  if (!data) return { title: undefined, subtitle: undefined, specs: undefined };
  const props = data.defaultProps || data;
  return {
    title: props.title,
    subtitle: props.subtitle,
    specs: Array.isArray(props.specs) ? props.specs : undefined,
  };
};

/**
 * Merges accommodations specs into rooms data by matching IDs.
 * Rooms are the source of truth: we iterate over rooms and overlay matching spec fields.
 */
const mergeAccommodationsWithRooms = (accommodationsSpecs, rooms) => {
  if (!rooms || !Array.isArray(rooms)) return undefined;
  const specs = Array.isArray(accommodationsSpecs) ? accommodationsSpecs : [];
  return rooms.map((room) => {
    const matchingSpec = specs.find(
      (s) => String(room.id) === String(s.id) || room.id === s.id
    );
    return matchingSpec ? { ...matchingSpec, ...room } : { ...room };
  });
};

// ── Sub-components ─────────────────────────────────────────────────────────

const FacilityIcon = ({ icon: Icon, label, value, color = 'primary.main', pluralForms }) => {
  if (value === false || value === null || value === undefined) return null;
  let displayLabel = label;
  if (typeof value === 'number' && pluralForms) {
    const [singular, plural1, plural2] = pluralForms;
    displayLabel = getPolishPlural(value, singular, plural1, plural2);
  }
  const displayText = typeof value === 'number' ? `${value} ${displayLabel}` : displayLabel;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color }}>
      <Icon sx={{ fontSize: 18 }} />
      <Typography variant="body2" component="span">{displayText}</Typography>
    </Box>
  );
};

const FacilityIcons = ({ item }) => {
  const maxOccupancy = item.maxOccupancy;
  const bedrooms   = getFieldValue(item, 'bedrooms');
  const bathrooms  = getFieldValue(item, 'bathrooms');
  const area       = getFieldValue(item, 'area');
  const hasAirCondition = getFieldValue(item, 'air condition') === true;
  const hasWifi         = getFieldValue(item, 'wi-fi') === true;
  const hasKitchen      = getFieldValue(item, 'kitchen') === true;
  const hasFireplace    = getFieldValue(item, 'fireplace') === true;
  const hasTerrace      = getFieldValue(item, 'terrace') === true;

  return (
    <Stack direction="row" flexWrap="wrap" gap={2} useFlexGap sx={{ mt: 1 }}>
      {area       && <FacilityIcon icon={AspectRatioIcon} label="m²"         value={area} />}
      {maxOccupancy && <FacilityIcon icon={PeopleIcon}  label="osób"         value={maxOccupancy} pluralForms={["osoba", "osoby", "osób"]} />}
      {bedrooms   && <FacilityIcon icon={BedIcon}       label="sypialnie"    value={bedrooms}     pluralForms={["sypialnia", "sypialnie", "sypialni"]} />}
      {bathrooms  && <FacilityIcon icon={BathtubIcon}   label="łazienki"     value={bathrooms}    pluralForms={["łazienka", "łazienki", "łazienek"]} />}
      {hasWifi         && <FacilityIcon icon={WifiIcon}      label="Wi‑Fi"       value={true} />}
      {hasAirCondition && <FacilityIcon icon={AcUnitIcon}    label="Klimatyzacja" value={true} />}
      {hasKitchen      && <FacilityIcon icon={KitchenIcon}   label="Kuchnia"      value={true} />}
      {hasFireplace    && <FacilityIcon icon={FireplaceIcon} label="Kominek"      value={true} />}
      {hasTerrace      && <FacilityIcon icon={BalconyIcon}   label="Taras"        value={true} />}
    </Stack>
  );
};

const AccommodationCardImageContent = ({ data }) => {
  const firstImageUrl = data?.pictureUrls?.[0];
  if (firstImageUrl) {
    return <CardMedia component="img" image={firstImageUrl} alt="" sx={{ height: 300, objectFit: 'cover' }} />;
  }
  return (
    <CardMedia component="div" sx={{ height: 300, backgroundColor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="body2" color="text.secondary">No Image</Typography>
    </CardMedia>
  );
};
const AccommodationCardImageWithQueryData = withQueryData(AccommodationCardImageContent);

const AccommodationCard = ({ item }) => {
  const propertyId = usePropertyId();
  const roomId = item.id;
  const { picturesQueryData } = useRoomPicturesData(propertyId, roomId, !!roomId);

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        borderColor: 'divider',
        transition: 'box-shadow 0.3s, border-color 0.3s',
        overflow: 'hidden',
        '&:hover': { boxShadow: 3, borderColor: 'primary.light' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {roomId
        ? <AccommodationCardImageWithQueryData queryData={picturesQueryData} />
        : (
          <CardMedia component="div" sx={{ height: 300, backgroundColor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">No Image</Typography>
          </CardMedia>
        )
      }
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
          {item.name}
        </Typography>
        {item.description && (
          <Typography variant="body1" color="text.secondary" paragraph>
            {item.description}
          </Typography>
        )}
        {Array.isArray(item.other) && item.other.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap sx={{ mt: 2 }}>
            {item.other.map((s, i) => <Chip key={i} label={s} size="small" variant="outlined" />)}
          </Stack>
        )}
        <Box sx={{ mt: 'auto', pt: 2 }}>
          <FacilityIcons item={item} />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Button component={Link} to={`/rooms/${roomId}`} variant="outlined" size="medium">
            Szczegóły
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// ── Inner component: receives rooms as data from withQueryData ─────────────

const AccommodationsInner = ({ data: rooms, title, subtitle, specs }) => {
  const mergedItems = mergeAccommodationsWithRooms(specs, rooms);
  if (!mergedItems || (mergedItems.length === 0 && !title)) return null;

  return (
    <Box sx={{ py: 8 }}>
      <Container>
        {title && (
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'justify', mb: 5, mx: 'auto' }}>
            {subtitle}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4, maxWidth: 960, mx: 'auto' }}>
          {mergedItems.map((item) => (
            <AccommodationCard key={item.id || item.name} item={item} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

const AccommodationsInnerWithRooms = withQueryData(AccommodationsInner);

// ── Pure component: receives website-content defaultProps via withWebsiteContent ──

/**
 * Pure Accommodations component — website-content props supplied by withWebsiteContent.
 *
 * Expected website-component defaultProps shape (accommodations.json):
 * {
 *   "title":    "...",
 *   "subtitle": "...",
 *   "specs": [{ "id": "<roomId>", "bedrooms": N, "bathrooms": N, ... }]
 * }
 *
 * Rooms are fetched from the rooms API and merged with specs (room is source of truth).
 */
export const AccommodationsPure = ({ id, title, subtitle, specs = [], images = [] }) => {
  const propertyId = usePropertyId();
  const { queryResults } = useRoomsData(propertyId);

  return (
    <Box id={id} component="section" sx={{ ...sectionScrollMarginSx, position: 'relative', minHeight: '400px', width: '100%' }}>
      <AccommodationsInnerWithRooms
        queryData={queryResults}
        title={title}
        subtitle={subtitle}
        specs={specs}
      />
    </Box>
  );
};

const Accommodations = withWebsiteContent(AccommodationsPure);
Accommodations.displayName = 'Accommodations';

export { AccommodationCard, FacilityIcons, mergeAccommodationsWithRooms, normalizeAccommodationsContent };
export default Accommodations;
